import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { profile } from "@/data/content";
import { useContent } from "@/i18n";

const linkDefs = [
  { key: "exploring", href: "#exploring" },
  { key: "about", href: "#about" },
  { key: "education", href: "#education" },
  { key: "experience", href: "#experience" },
  { key: "research", href: "#research" },
  { key: "gallery", href: "#gallery" },
  { key: "journal", href: "/journal", route: true },
  { key: "contact", href: "#contact" },
];

export const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { ui, lang, setLang } = useContent();
  const links = linkDefs.map((d) => ({ ...d, label: ui.nav[d.key] }));

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.9, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 ${
        scrolled ? "bg-cream/85 backdrop-blur-xl border-b border-line" : "bg-transparent"
      }`}
      data-testid="main-nav"
    >
      <div className="max-w-7xl mx-auto px-6 h-[72px] flex items-center justify-between">
        <a href="#top" className="font-serif text-xl tracking-tight" data-testid="nav-logo">
          Sahithi <span className="italic text-cobalt">Rasakonda</span>
        </a>

        <nav className="hidden lg:flex items-center gap-7">
          {links.map((l) =>
            l.route ? (
              <Link
                key={l.label}
                to={l.href}
                data-testid={`nav-link-${l.key}`}
                className="link-underline text-xs tracking-[0.18em] uppercase font-bold text-cocoa hover:text-choco transition-colors duration-300"
              >
                {l.label}
              </Link>
            ) : (
              <a
                key={l.label}
                href={l.href}
                data-testid={`nav-link-${l.key}`}
                className="link-underline text-xs tracking-[0.18em] uppercase font-bold text-cocoa hover:text-choco transition-colors duration-300"
              >
                {l.label}
              </a>
            )
          )}
          <div className="flex items-center rounded-full border border-line overflow-hidden" data-testid="lang-toggle">
            <button onClick={() => setLang("en")} data-testid="lang-en-button" className={`px-3 py-2 text-[10px] tracking-[0.15em] font-bold transition-colors duration-300 ${lang === "en" ? "bg-choco text-cream" : "text-cocoa hover:text-choco"}`}>EN</button>
            <button onClick={() => setLang("de")} data-testid="lang-de-button" className={`px-3 py-2 text-[10px] tracking-[0.15em] font-bold transition-colors duration-300 ${lang === "de" ? "bg-cobalt text-cream" : "text-cocoa hover:text-choco"}`}>DE</button>
          </div>
          <a
            href={profile.cvUrl}
            download
            data-testid="nav-cv-button"
            className="group inline-flex items-center gap-2 rounded-full border-2 border-cobalt text-cobalt px-5 py-1.5 text-xs tracking-[0.18em] uppercase font-bold hover:bg-cobalt hover:text-cream transition-colors duration-300"
          >
            {ui.nav.cv}
            <ArrowUpRight size={14} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </a>
        </nav>

        <button
          className="lg:hidden p-2"
          onClick={() => setOpen(!open)}
          aria-label="Toggle menu"
          data-testid="nav-menu-toggle"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="lg:hidden overflow-hidden bg-cream/95 backdrop-blur-xl border-b border-line"
            data-testid="nav-mobile-menu"
          >
            <div className="px-6 py-6 flex flex-col gap-4">
              {links.map((l) =>
                l.route ? (
                  <Link
                    key={l.label}
                    to={l.href}
                    onClick={() => setOpen(false)}
                    className="font-serif text-2xl tracking-tight"
                  >
                    {l.label}
                  </Link>
                ) : (
                  <a key={l.label} href={l.href} onClick={() => setOpen(false)} className="font-serif text-2xl tracking-tight">
                    {l.label}
                  </a>
                )
              )}
              <div className="flex gap-3 pt-2">
                <button onClick={() => setLang("en")} data-testid="lang-en-mobile" className={`flex-1 rounded-full border py-2.5 text-xs tracking-[0.2em] font-bold transition-colors duration-300 ${lang === "en" ? "bg-choco text-cream border-choco" : "border-line text-cocoa"}`}>EN</button>
                <button onClick={() => setLang("de")} data-testid="lang-de-mobile" className={`flex-1 rounded-full border py-2.5 text-xs tracking-[0.2em] font-bold transition-colors duration-300 ${lang === "de" ? "bg-cobalt text-cream border-cobalt" : "border-line text-cocoa"}`}>DE</button>
              </div>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
